import { Link, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import {
  Menu, X, CircleUserRound, Phone, MapPin, ChevronDown,
  Facebook, Instagram, Linkedin, Clock, Sparkles, Target, Megaphone,
  Wrench, Rocket, Network, ArrowRight,
} from "lucide-react";
import api from "@/lib/api";
import { isPortalAuthed, getPortalUser } from "@/lib/portalAuth";

/* -------- TikTok icon (lucide doesn't ship it) -------- */
function TikTokIcon({ size = 14, className = "" }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} className={className} fill="currentColor" aria-hidden="true">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-.88-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43V9a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.43z" />
    </svg>
  );
}

const SOCIALS = [
  { name: "Facebook",  href: "https://www.facebook.com/bessonsolutions",  Icon: Facebook  },
  { name: "Instagram", href: "https://www.instagram.com/besson_solutions", Icon: Instagram },
  { name: "TikTok",    href: "https://www.tiktok.com/@besson_solutions",  Icon: TikTokIcon },
  { name: "LinkedIn",  href: "https://www.linkedin.com/company/besson-solutions", Icon: Linkedin },
];

/* -------- Services mega-menu (grouped by moment, no duplicates) -------- */
const MOMENTS = [
  {
    key: "preparation",
    title: "Préparation d'un projet",
    color: "#FFDCA8",
    desc: "Cadrer l'idée, le positionnement et le plan d'action.",
    services: [
      { id: "identite",              icon: Sparkles,  label: "Identité & positionnement" },
      { id: "strategie_commerciale", icon: Target,    label: "Stratégie commerciale" },
      { id: "strategie_marketing",   icon: Megaphone, label: "Stratégie marketing" },
      { id: "consulting",            icon: Clock,     label: "Consulting à l'heure" },
    ],
  },
  {
    key: "creation",
    title: "Création d'entreprise",
    color: "#A7F3D0",
    desc: "Lancer la marque et les outils qui structurent l'activité.",
    services: [
      { id: "outils",                icon: Wrench,    label: "Outils métier, plateforme, app" },
      { id: "acquisition",           icon: Rocket,    label: "Développement commercial" },
    ],
  },
  {
    key: "developpement",
    title: "Développement d'entreprise",
    color: "#67E8F9",
    desc: "Scaler le modèle, dupliquer en licences ou en franchises.",
    services: [
      { id: "deploiement",           icon: Network,   label: "Licences & franchises" },
    ],
  },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hours, setHours] = useState(null);
  const [user, setUser] = useState(null);
  const loc = useLocation();
  const megaRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => { setOpen(false); setServicesOpen(false); setMobileServicesOpen(false); }, [loc.pathname]);

  useEffect(() => {
    const fetchHours = () => api.get("/business-hours/status").then((r) => setHours(r.data)).catch(() => {});
    fetchHours();
    const t = setInterval(fetchHours, 60_000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const sync = () => setUser(isPortalAuthed() ? getPortalUser() : null);
    sync();
    window.addEventListener("storage", sync);
    return () => window.removeEventListener("storage", sync);
  }, [loc.pathname]);

  // Close mega-menu on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (megaRef.current && !megaRef.current.contains(e.target)) setServicesOpen(false);
    };
    if (servicesOpen) window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, [servicesOpen]);

  const firstName = (user?.full_name || "").split(" ")[0] || user?.email?.split("@")[0] || "Mon espace";

  const logoSize = scrolled
    ? "w-[46px] h-[46px] sm:w-[52px] sm:h-[52px]"
    : "w-[82px] h-[82px] sm:w-[110px] sm:h-[110px]";
  const pillPadding = scrolled
    ? "pl-[36px] sm:pl-[58px]"
    : "pl-[82px] sm:pl-[110px]";

  return (
    <header data-testid="site-header" data-scrolled={scrolled ? "true" : "false"} className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${scrolled ? "pt-1" : "pt-2"}`}>
      <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8">
        {/* Top utility bar — single centered row: hours · phone · socials · location. Hidden when scrolled. */}
        <div
          data-testid="header-utility-bar"
          className={`overflow-hidden transition-all duration-500 flex justify-center ${scrolled ? "max-h-0 opacity-0 mb-0" : "max-h-24 opacity-100 mb-2"}`}
        >
          <div className="inline-flex flex-wrap items-center justify-center gap-x-4 gap-y-1 bg-[#0A1128]/85 backdrop-blur-md rounded-full px-4 py-1 text-white text-[12px] shadow-lg shadow-[#0A1128]/30">
            <span data-testid="header-hours" style={{ fontFamily: "'Poppins', sans-serif" }} className="inline-flex items-center gap-1.5 text-white/85">
              <Clock size={12} className="text-[#67E8F9]" />
              <span><span className="font-bold">Lun–Ven</span> 9h–12h · 14h–18h</span>
              {hours?.is_open && <span className="ml-1 inline-block w-2 h-2 rounded-full bg-[#A7F3D0] animate-pulse" title="Ouvert" />}
            </span>

            {hours?.phone && (
              <>
                <span aria-hidden className="hidden sm:inline-block w-px h-3 bg-white/20" />
                <a
                  href={`tel:${(hours.phone || "").replace(/\s/g, "")}`}
                  data-testid="header-phone"
                  style={{ fontFamily: "'Poppins', sans-serif" }}
                  className="group inline-flex items-center gap-2 text-white hover:text-[#67E8F9] transition-colors"
                >
                  <span className="relative inline-flex items-center justify-center w-6 h-6 rounded-full border border-white/40 group-hover:border-[#67E8F9]/70 transition-colors">
                    <Phone size={11} />
                  </span>
                  <span className="text-[13px] sm:text-[14px] font-bold tabular-nums tracking-wide">{hours.phone}</span>
                </a>
              </>
            )}

            <span aria-hidden className="hidden lg:inline-block w-px h-3 bg-white/20" />

            <span
              data-testid="header-location"
              style={{ fontFamily: "'Poppins', sans-serif" }}
              className="hidden lg:inline-flex items-center gap-1 text-[11px] text-white/75"
            >
              <MapPin size={11} className="text-[#67E8F9]" />
              <span>Istres · <span className="text-white/55">Sud-Est</span></span>
            </span>
          </div>
        </div>

        {/* Main pill bar */}
        <div className="relative">
          {/* Floating logo */}
          <Link to="/" data-testid="header-logo-link" className="absolute -left-1 sm:left-0 top-1/2 -translate-y-1/2 z-10 group" aria-label="BESSON SOLUTIONS">
            <div className="relative">
              <span className={`absolute inset-0 -m-1 rounded-full bg-gradient-to-br from-[#67E8F9]/30 to-[#FFDCA8]/40 blur-xl transition-opacity duration-500 ${scrolled ? "opacity-50" : "opacity-80"} group-hover:opacity-100`} />
              <img
                src="https://customer-assets.emergentagent.com/job_3a2b13f5-84ee-42a0-a8c4-4ac266af26a7/artifacts/8jhfiwyn_BESSONSOLUTION-logo-avatar.png"
                alt="BESSON SOLUTIONS"
                className={`relative rounded-full border-[3px] border-white shadow-2xl bg-white object-cover group-hover:rotate-3 group-hover:scale-105 transition-all duration-500 ease-out ${logoSize}`}
              />
            </div>
          </Link>

          {/* Glass pill */}
          <div className={`ml-7 sm:ml-14 glass rounded-full ${pillPadding} pr-2 sm:pr-3 py-2.5 sm:py-3 flex items-center gap-2 transition-all duration-500 ${scrolled ? "min-h-[56px]" : "min-h-[60px] sm:min-h-[64px]"}`}>

            {/* Brand text */}
            <Link to="/" data-testid="header-brand-text"
              className={`flex flex-col leading-none overflow-hidden transition-all duration-500 opacity-100 max-w-[180px] mr-auto lg:mx-0 lg:mr-auto ${scrolled ? "lg:opacity-100 lg:max-w-[200px] lg:ml-0" : "lg:opacity-0 lg:max-w-0 lg:-ml-2 lg:pointer-events-none"}`}
              aria-label="BESSON SOLUTIONS">
              <span className="display font-extrabold tracking-tight text-[#0A1128] text-[14px] sm:text-[17px]">BESSON</span>
              <span className="tracking-[0.28em] text-[#06B6D4] font-semibold text-[9px] sm:text-[11px] mt-0.5">SOLUTIONS</span>
            </Link>

            {/* Desktop nav */}
            <nav className={`hidden lg:flex items-center transition-all ${scrolled ? "ml-0" : "ml-2"}`}>
              {/* Services mega-menu */}
              <div className="relative flex items-center" ref={megaRef}>
                <button
                  type="button"
                  data-testid="nav-services"
                  onClick={() => setServicesOpen((o) => !o)}
                  aria-expanded={servicesOpen}
                  className="link-underline text-[16px] font-bold text-[#0A1128] hover:text-[#06B6D4] transition-colors px-5 py-1 inline-flex items-center gap-1"
                >
                  Services <ChevronDown size={14} className={`transition-transform ${servicesOpen ? "rotate-180" : ""}`} />
                </button>
                <span aria-hidden="true" className="block w-px h-4 bg-[#67E8F9]/55" />

                {/* Mega panel */}
                <div
                  className={`absolute top-full left-0 mt-3 w-[min(960px,90vw)] origin-top transition-all duration-200 ${
                    servicesOpen ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 -translate-y-2 pointer-events-none"
                  }`}
                  data-testid="services-mega-menu"
                >
                  <div className="bg-white rounded-3xl shadow-2xl border border-black/8 overflow-hidden">
                    <div className="grid grid-cols-3 gap-0 divide-x divide-black/5">
                      {MOMENTS.map((m) => (
                        <div key={m.key} className="p-5">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="inline-block w-2 h-2 rounded-full" style={{ background: m.color }} />
                            <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#64748B]">{m.title}</span>
                          </div>
                          <p className="text-[12px] text-[#64748B] mb-3 leading-relaxed">{m.desc}</p>
                          <ul className="space-y-1">
                            {m.services.map((s) => (
                              <li key={s.id}>
                                <Link
                                  to={`/services/${s.id}`}
                                  data-testid={`mega-service-${m.key}-${s.id}`}
                                  onClick={() => setServicesOpen(false)}
                                  className="flex items-center gap-2 px-3 py-2 rounded-xl text-[13px] text-[#0A1128] font-bold hover:bg-[#06B6D4]/8 hover:text-[#06B6D4] transition-colors group"
                                >
                                  <s.icon size={14} className="text-[#06B6D4] shrink-0" />
                                  <span className="flex-1">{s.label}</span>
                                  <ArrowRight size={12} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                    <div className="bg-[#FAFAFA] px-5 py-3 border-t border-black/5 flex items-center justify-between flex-wrap gap-2 text-[12px]">
                      <Link to="/services" onClick={() => setServicesOpen(false)} data-testid="mega-services-all" className="inline-flex items-center gap-1 text-[#0A1128] font-bold hover:text-[#06B6D4]">
                        Voir tous nos services →
                      </Link>
                      <Link to="/rendez-vous" onClick={() => setServicesOpen(false)} className="inline-flex items-center gap-1 px-4 py-1.5 rounded-full bg-[#0A1128] hover:bg-[#14213d] text-white font-bold transition-colors">
                        <Phone size={11} /> Prendre RDV
                      </Link>
                    </div>
                  </div>
                </div>
              </div>

              <Link to="/qui-sommes-nous" data-testid="nav-qui-sommes-nous" className="link-underline text-[16px] font-bold text-[#0A1128] hover:text-[#06B6D4] transition-colors px-5 py-1">Qui sommes-nous</Link>
              <span aria-hidden="true" className="block w-px h-4 bg-[#67E8F9]/55" />
              <Link to="/etudes-de-cas" data-testid="nav-etudes-de-cas" className="link-underline text-[16px] font-bold text-[#0A1128] hover:text-[#06B6D4] transition-colors px-5 py-1">Études de cas</Link>
              <span aria-hidden="true" className="block w-px h-4 bg-[#67E8F9]/55" />
              <a href="/#contact" data-testid="nav-contact" className="link-underline text-[16px] font-bold text-[#0A1128] hover:text-[#06B6D4] transition-colors px-5 py-1">Contact</a>
            </nav>

            <div className="flex items-center gap-2 lg:ml-auto shrink-0">
              {/* Primary CTA */}
              <Link to="/rendez-vous" data-testid="header-rdv-cta" data-track="header-rdv-cta"
                className="inline-flex items-center gap-1.5 px-3 sm:px-5 py-2 sm:py-2.5 rounded-full text-[12px] sm:text-[14px] font-extrabold bg-[#06B6D4] lg:bg-white text-[#0A1128] hover:bg-[#67E8F9] lg:hover:bg-[#67E8F9] transition-colors shadow-md border lg:border-[#06B6D4]/30 whitespace-nowrap">
                <Phone size={14} />
                <span className="hidden sm:inline">Prendre RDV en ligne</span>
                <span className="sm:hidden">RDV</span>
              </Link>

              {/* Espace Entreprise — always visible */}
              <Link
                to={user ? "/portail" : "/portail/connexion"}
                data-testid="header-portal-icon"
                data-track="header-portal-cta"
                aria-label={user ? `Espace de ${firstName}` : "Connexion à mon espace entreprise"}
                className={`inline-flex items-center justify-center gap-2 rounded-full bg-[#06B6D4] hover:bg-[#67E8F9] border border-[#06B6D4] text-[#0A1128] transition-all shrink-0 shadow-md font-bold ${
                  user ? "h-9 pl-1 pr-3" : "w-9 h-9 lg:w-auto lg:px-3.5 lg:h-9"
                }`}
              >
                {user ? (
                  <>
                    {user.avatar_url ? (
                      <img src={user.avatar_url} alt="" className="w-7 h-7 rounded-full object-cover" />
                    ) : (
                      <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#0A1128] text-[#06B6D4] font-extrabold text-[11px]">
                        {firstName.slice(0, 1).toUpperCase()}
                      </span>
                    )}
                    <span data-testid="header-user-firstname" className="font-bold text-[13px] hidden sm:inline">{firstName}</span>
                  </>
                ) : (
                  <>
                    <CircleUserRound size={18} />
                    <span className="hidden lg:inline text-[13px] whitespace-nowrap">Espace Entreprise</span>
                  </>
                )}
              </Link>

              {/* Mobile menu toggle — RIGHT side, after Espace icon */}
              <button
                data-testid="mobile-menu-toggle"
                onClick={() => setOpen((o) => !o)}
                className="lg:hidden p-2 rounded-full text-[#0A1128] hover:bg-black/5 shrink-0"
                aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
                aria-expanded={open}
                aria-controls="mobile-menu"
              >
                {open ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {open && (
          <div className="lg:hidden glass mt-2 rounded-3xl p-4 flex flex-col gap-1" id="mobile-menu" data-testid="mobile-menu">
            {/* Services collapsible */}
            <button
              type="button"
              data-testid="mobile-nav-services-toggle"
              onClick={() => setMobileServicesOpen((o) => !o)}
              className="flex items-center justify-between px-3 py-2 rounded-lg hover:bg-black/5 text-[#0A1128] font-semibold w-full"
            >
              <span>Services</span>
              <ChevronDown size={14} className={`transition-transform ${mobileServicesOpen ? "rotate-180" : ""}`} />
            </button>
            {mobileServicesOpen && (
              <div className="pl-3 pb-2 space-y-3 border-l-2 border-[#06B6D4]/30 ml-3">
                {MOMENTS.map((m) => (
                  <div key={m.key}>
                    <div className="text-[10px] font-bold tracking-[0.2em] uppercase mb-1.5 mt-1" style={{ color: m.color === "#FFDCA8" ? "#92400E" : m.color === "#A7F3D0" ? "#065F46" : "#06B6D4" }}>
                      {m.title}
                    </div>
                    <ul className="space-y-0.5">
                      {m.services.map((s) => (
                        <li key={s.id}>
                          <Link to={`/services/${s.id}`} data-testid={`mobile-mega-${m.key}-${s.id}`} className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-[#06B6D4]/8 text-[13px] text-[#0A1128] font-medium">
                            <s.icon size={12} className="text-[#06B6D4]" /> {s.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}

            <Link to="/qui-sommes-nous" data-testid="mobile-nav-qui-sommes-nous" className="px-3 py-2 rounded-lg hover:bg-black/5 text-[#0A1128] font-semibold">Qui sommes-nous</Link>
            <Link to="/etudes-de-cas" data-testid="mobile-nav-etudes-de-cas" className="px-3 py-2 rounded-lg hover:bg-black/5 text-[#0A1128] font-semibold">Études de cas</Link>
            <a href="/#contact" data-testid="mobile-nav-contact" className="px-3 py-2 rounded-lg hover:bg-black/5 text-[#0A1128] font-semibold">Contact</a>

            <div className="grid grid-cols-2 gap-2 mt-2">
              <Link to="/rendez-vous" data-testid="mobile-rdv-cta" data-track="mobile-rdv-cta" className="px-3 py-2.5 rounded-full bg-[#06B6D4] hover:bg-[#67E8F9] text-[#0A1128] font-bold text-[13px] flex items-center justify-center gap-1.5 shadow-md transition-colors">
                <Phone size={13} /> Prendre RDV
              </Link>
              <Link to={user ? "/portail" : "/portail/connexion"} data-testid="mobile-espace-cta" data-track="mobile-espace-cta" className="px-3 py-2.5 rounded-full border-2 border-[#0A1128]/15 hover:border-[#06B6D4] text-[#0A1128] font-bold text-[13px] flex items-center justify-center gap-1.5 bg-white transition-colors">
                <CircleUserRound size={13} /> {user ? firstName : "Mon espace"}
              </Link>
            </div>

            {/* Socials in mobile menu */}
            <div className="mt-3 flex items-center justify-center gap-2 pt-2 border-t border-black/5">
              {SOCIALS.map((s) => (
                <a key={s.name} href={s.href} target="_blank" rel="noreferrer" aria-label={s.name} className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-white border border-black/8 hover:border-[#06B6D4] hover:text-[#06B6D4] text-[#0A1128] transition-colors">
                  <s.Icon size={14} />
                </a>
              ))}
            </div>
            <div className="mt-1 text-center text-[11px] text-[#64748B] flex items-center justify-center gap-1">
              <Clock size={11} className="text-[#06B6D4]" /> <span className="font-bold">Lun–Ven</span> 9h–12h · 14h–18h
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
